import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useFormikContext } from 'formik';
import { Row, Col, Form } from 'react-bootstrap';
import { DateTime } from 'luxon';

import { afterToday } from 'utils/Time';

import Select from './Select';
import DatePickerField from './DatePickerField';

interface IRangeDatePickerForm {
  period: IPeriod;
  startDate: Date;
  endDate: Date;
}

interface IRangeDatePickerProps {
  includeAll?: boolean;
  dataTestId?: string;
  limitMaxDateToday?: boolean;
  monthBaseOnly?: boolean;
}

const RangeDatePicker = ({
  includeAll = false,
  limitMaxDateToday = true,
  dataTestId,
  monthBaseOnly = false
}: IRangeDatePickerProps) => {
  const intl = useIntl();
  const { setFieldValue, values } = useFormikContext<IRangeDatePickerForm>();

  const periodOptions = () => {
    const options: IPeriodOption[] = [
      {
        label: intl.formatMessage({ id: 'prevMonth' }),
        value: 'previous-month'
      },
      {
        label: intl.formatMessage({ id: 'MonthToDate' }),
        value: 'month-to-date'
      },
      {
        label: intl.formatMessage({ id: 'last30daysKey' }),
        value: '30d'
      },
      {
        label: intl.formatMessage({ id: 'last90daysKey' }),
        value: '90d'
      },
      {
        label: intl.formatMessage({ id: 'monthlyKey' }),
        value: 'monthly'
      },
      {
        label: intl.formatMessage({ id: 'yearlyKey' }),
        value: 'yearly'
      },
      {
        label: intl.formatMessage({ id: 'customDateRangeKey' }),
        value: 'custom_range'
      }
    ];
    if (includeAll) {
      options.push({ label: intl.formatMessage({ id: 'timeAll' }), value: 'all' });
    }
    if (monthBaseOnly) {
      const nonMonthly = ['30d', '90d', 'yearly', 'custom_range', 'all'];
      return options.filter((option) => !nonMonthly.includes(option.value));
    }
    return options;
  };

  const updateDateInterval = useCallback(
    (startDate: DateTime, endDate: DateTime) => {
      if (!DateTime.fromJSDate(values.startDate).hasSame(startDate, 'day')) {
        setFieldValue('startDate', startDate.toJSDate());
      }
      if (!DateTime.fromJSDate(values.endDate).hasSame(endDate, 'day')) {
        setFieldValue('endDate', endDate.toJSDate());
      }
    },
    [values.startDate, values.endDate, setFieldValue]
  );

  const handleStartDate = useCallback(
    (value: Date) => {
      switch (values.period) {
        case 'previous-month':
          updateDateInterval(
            DateTime.now().startOf('month').minus({ months: 1 }),
            DateTime.now().startOf('month').minus({ days: 1 })
          );
          break;
        case 'month-to-date':
          updateDateInterval(DateTime.now().startOf('month'), DateTime.now());
          break;
        case '30d':
          updateDateInterval(DateTime.now().minus({ days: 29 }), DateTime.now());
          break;
        case '90d':
          updateDateInterval(DateTime.now().minus({ days: 89 }), DateTime.now());
          break;
        case 'daily':
          updateDateInterval(DateTime.now(), DateTime.now());
          break;
        case 'weekly':
          updateDateInterval(
            DateTime.fromJSDate(value).startOf('week'),
            limitMaxDateToday ? afterToday(value, 'week') : DateTime.fromJSDate(value).endOf('week')
          );
          break;
        case 'monthly':
          updateDateInterval(
            DateTime.fromJSDate(value).startOf('month'),
            limitMaxDateToday
              ? afterToday(value, 'month')
              : DateTime.fromJSDate(value).endOf('month')
          );
          break;
        case 'yearly':
          updateDateInterval(
            DateTime.fromJSDate(value).startOf('year'),
            limitMaxDateToday ? afterToday(value, 'year') : DateTime.fromJSDate(value).endOf('year')
          );
          break;
        case 'all':
          updateDateInterval(DateTime.fromMillis(0), DateTime.now());
          break;
      }
    },
    [updateDateInterval, values.period, limitMaxDateToday]
  );

  const handleMaxDate = () => {
    if (!limitMaxDateToday) return null;
    switch (values.period) {
      case 'yearly':
        return DateTime.now().endOf('year').toJSDate();
      case 'custom_range':
        return values.endDate;
      default:
        return DateTime.now().toJSDate();
    }
  };

  useEffect(() => {
    if (values.period !== 'custom_range') {
      handleStartDate(values.startDate);
    }
  }, [values.period, values.startDate, handleStartDate, updateDateInterval]);

  return (
    <>
      <Row>
        <Form.Group className="form-group" as={Col} md={12} lg={12}>
          <Select
            name="period"
            label={intl.formatMessage({ id: 'dateRangeKey' })}
            isSearchable={false}
            placeholder={intl.formatMessage({ id: 'dateRangeKey' })}
            options={periodOptions()}
            variant="form"
            required
            dataTestId={`${dataTestId}-dropdown`}
          />
        </Form.Group>
      </Row>
      <Row>
        {!monthBaseOnly &&
          ['weekly', 'monthly', 'yearly', 'custom_range'].includes(values.period) && (
            <Form.Group className="form-group" as={Col} md={12} lg={6}>
              <DatePickerField
                name="startDate"
                title="start"
                variant="primary"
                dateFormat={
                  values.period === 'monthly'
                    ? 'MMMM yyyy'
                    : values.period === 'yearly'
                      ? 'yyyy'
                      : undefined
                }
                showMonthYearPicker={values.period === 'monthly'}
                showFullMonthYearPicker={values.period === 'monthly'}
                showYearPicker={values.period === 'yearly'}
                maxDate={handleMaxDate()}
                className="w-100"
                wrapperClassName="w-100"
                dataTestId={`${dataTestId}-calendar`}
              />
            </Form.Group>
          )}
        {monthBaseOnly && ['monthly'].includes(values.period) && (
          <Form.Group className="form-group" as={Col} md={12} lg={6}>
            <DatePickerField
              name="startDate"
              title="start"
              variant="primary"
              dateFormat={
                values.period === 'monthly'
                  ? 'MMMM yyyy'
                  : values.period === 'yearly'
                    ? 'yyyy'
                    : undefined
              }
              showMonthYearPicker={true}
              showFullMonthYearPicker={false}
              showYearPicker={false}
              maxDate={handleMaxDate()}
              className="w-100"
              wrapperClassName="w-100"
              dataTestId={`${dataTestId}-calendar`}
            />
          </Form.Group>
        )}
        {values.period === 'custom_range' && (
          <Form.Group className="form-group" as={Col} md={12} lg={6}>
            <DatePickerField
              title="end"
              name="endDate"
              variant="primary"
              selectsEnd
              startDate={values.startDate}
              maxDate={limitMaxDateToday ? DateTime.now().toJSDate() : null}
              endDate={values.endDate}
              minDate={values.startDate}
              className="w-100"
              wrapperClassName="w-100"
              dataTestId={`${dataTestId}-calendar-end`}
            />
          </Form.Group>
        )}
      </Row>
    </>
  );
};

export default RangeDatePicker;
